import type { InitiativeListItem, Lifecycle, PaginatedResponse } from '@/domain/types'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'

export interface InitiativesQuery {
  page?: number
  pageSize?: number
  /** Filter by exact domain. */
  domainId?: number
  /** Filter by exact capability. Pairs with domainId or stands alone. */
  capabilityId?: number
  /**
   * Filter by stage code(s). Multi-value — repeats the param key:
   * `?stageCode=Idea&stageCode=Shaped`. Server matches `IN (...)`.
   */
  stageCode?: string[]
  /** Filter by business-unit finance code(s). Multi-value. */
  businessUnitCode?: string[]
  /** Filter by blocked-reason code(s). Multi-value. */
  blockedReason?: string[]
  /** Filter by AI Act class code(s). Multi-value. */
  aiActClass?: string[]
  /** Derived axis — `active`, `blocked` or `closed`. */
  lifecycle?: Lifecycle
  /** Filter by owner Entra Object ID. Exact GUID match server-side. */
  ownerObjectId?: string
  q?: string
  includeInactive?: boolean
  asOf?: string
}

/**
 * `GET /api/initiatives` with the standard pagination envelope.
 *
 * Filters are reflected in the URL so the path-keyed hook cache re-fires
 * naturally when a filter changes.
 *
 * The Article 50 register is this hook with
 * `{ stageCode: ['Operate'], aiActClass: ['Article50'] }` — a saved filter,
 * not a bespoke endpoint.
 */
export function useInitiatives(
  query: InitiativesQuery,
): QueryState<PaginatedResponse<InitiativeListItem>> {
  return useApiQuery<PaginatedResponse<InitiativeListItem>>(buildInitiativesPath(query))
}

export function buildInitiativesPath(q: InitiativesQuery): string {
  const params = new URLSearchParams()
  if (q.page !== undefined) params.set('page', String(q.page))
  if (q.pageSize !== undefined) params.set('pageSize', String(q.pageSize))
  if (q.domainId !== undefined) params.set('domainId', String(q.domainId))
  if (q.capabilityId !== undefined) params.set('capabilityId', String(q.capabilityId))
  appendAll(params, 'stageCode', q.stageCode)
  appendAll(params, 'businessUnitCode', q.businessUnitCode)
  appendAll(params, 'blockedReason', q.blockedReason)
  appendAll(params, 'aiActClass', q.aiActClass)
  if (q.lifecycle) params.set('lifecycle', q.lifecycle)
  if (q.ownerObjectId) params.set('ownerObjectId', q.ownerObjectId)
  if (q.q) params.set('q', q.q)
  if (q.includeInactive) params.set('includeInactive', 'true')
  if (q.asOf) params.set('asOf', q.asOf)
  const qs = params.toString()
  return qs ? `/api/initiatives?${qs}` : '/api/initiatives'
}

function appendAll(params: URLSearchParams, key: string, values: string[] | undefined): void {
  if (!values || values.length === 0) return
  for (const value of values) params.append(key, value)
}
